import React, { useState, useEffect, useRef } from 'react';
import useReducedMotion from '../../hooks/useReducedMotion';

const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const MORPH_DURATION = 500;
const DISPLAY_DURATION = 3000;

// Persist animation state across theme-triggered remounts
let persistedIndex = 0;
let persistedText = '';
let persistedPhase = 'idle';

export default function MorphingText({ texts, delay = 0, className }) {
  const [displayed, setDisplayed] = useState(persistedText || '');
  const [started, setStarted] = useState(persistedPhase !== 'idle' || !!persistedText);
  const currentIndexRef = useRef(persistedIndex);
  const currentTextRef = useRef(persistedText || texts[0]);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const phaseRef = useRef(persistedPhase); // 'initial' | 'morphing' | 'idle'
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const timer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  // Initial scramble-in (skip if already persisted past initial)
  useEffect(() => {
    if (!started) return;

    // If we already have persisted text, skip the initial animation
    if (persistedText) {
      setDisplayed(persistedText);
      currentTextRef.current = persistedText;
      phaseRef.current = 'idle';
      return;
    }

    if (reducedMotion) {
      setDisplayed(texts[0]);
      currentTextRef.current = texts[0];
      phaseRef.current = 'idle';
      persistedText = texts[0];
      persistedPhase = 'idle';
      return;
    }

    phaseRef.current = 'initial';
    const startTime = performance.now();
    const targetText = texts[0];
    const duration = 600;

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      let result = '';
      for (let i = 0; i < targetText.length; i++) {
        const charProgress = (progress * targetText.length - i);
        if (targetText[i] === ' ' || targetText[i] === '·') {
          result += targetText[i];
        } else if (charProgress >= 1) {
          result += targetText[i];
        } else {
          result += SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
        }
      }

      setDisplayed(result);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayed(targetText);
        currentTextRef.current = targetText;
        phaseRef.current = 'idle';
        persistedText = targetText;
        persistedIndex = 0;
        persistedPhase = 'idle';
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [started, texts, reducedMotion]);

  // Cycling morph after initial reveal
  useEffect(() => {
    if (!started || reducedMotion) return;

    // Wait for initial animation to finish, then start cycling
    const startCycling = () => {
      if (phaseRef.current !== 'idle') {
        // Re-check until initial is done
        setTimeout(startCycling, 100);
        return;
      }

      const cycle = () => {
        if (phaseRef.current === 'morphing') return;
        phaseRef.current = 'morphing';

        currentIndexRef.current = (currentIndexRef.current + 1) % texts.length;
        persistedIndex = currentIndexRef.current;
        const fromText = currentTextRef.current;
        const targetText = texts[currentIndexRef.current];
        const startTime = performance.now();
        const maxLen = Math.max(fromText.length, targetText.length);

        const animate = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / MORPH_DURATION, 1);

          let result = '';
          for (let i = 0; i < maxLen; i++) {
            const charProgress = (progress * maxLen - i) / 1.5;
            const targetChar = i < targetText.length ? targetText[i] : '';
            const fromChar = i < fromText.length ? fromText[i] : '';

            // Preserve separators and spaces
            if (targetChar === ' ' || targetChar === '·' || fromChar === ' ' || fromChar === '·') {
              result += targetChar || fromChar;
            } else if (charProgress >= 1 && targetChar) {
              result += targetChar;
            } else if (charProgress > 0) {
              result += SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
            } else if (fromChar) {
              result += fromChar;
            }
          }

          setDisplayed(result);

          if (progress < 1) {
            rafRef.current = requestAnimationFrame(animate);
          } else {
            setDisplayed(targetText);
            currentTextRef.current = targetText;
            phaseRef.current = 'idle';
            persistedText = targetText;
            persistedPhase = 'idle';
          }
        };

        rafRef.current = requestAnimationFrame(animate);
      };

      timerRef.current = setInterval(cycle, DISPLAY_DURATION + MORPH_DURATION);
    };

    // Small delay to let initial animation finish (shorter if resuming)
    const initTimer = setTimeout(startCycling, persistedText ? 100 : 800);

    return () => {
      clearTimeout(initTimer);
      clearInterval(timerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [started, texts, reducedMotion]);

  if (!started) {
    return <span className={className} style={{ visibility: 'hidden' }}>{texts[0]}</span>;
  }

  if (reducedMotion) {
    return <span className={className}>{texts[0]}</span>;
  }

  return (
    <span className={className} style={{ position: 'relative', display: 'inline-block' }}>
      <span style={{ visibility: 'hidden' }} aria-hidden="true">{texts.reduce((a, b) => a.length >= b.length ? a : b)}</span>
      <span style={{ position: 'absolute', left: 0, top: 0, width: '100%', textAlign: 'center' }}>{displayed}</span>
    </span>
  );
}
